import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useEstimatePrices } from '@/hooks/useEstimatePrices';
import { EstimateDayStepCardProps } from './EstimateDayStepCard';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SelectedEstimateOption } from '../../../types/estimates/SelectedEstimateOptionType';
import { getHotelCategoryLabel } from '@/utils/benefits/getHotelCategoryName';
import {
  EstimateHotelCategoriesProps,
  useEstimateHotelCategories,
} from '@/hooks/useEstimateHotel';
import EstimateHotelBenefitRoomOptions from './EstimateHotelBenefitRoomOptions';
import { useGetFileUrl } from '@/hooks/useFile';
import EstimateDayStepCardTemplate from './EstimateDayStepCardTemplate';
import EstimateOptionalBenefitCell, {
  EstimateOptionalBenefitCellProps,
} from './EstimateOptionalBenefitCell';
import { sanitizeDeltaPrice } from '@/utils/circuits/prices/formatPrice';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { useTranslation } from '@/i18n';

interface EstimateDayHotelStepCardProps extends EstimateDayStepCardProps {
  adultParticipants: EstimateFormData['adults'];
  childParticipants: EstimateFormData['children'];
}

const EstimateDayHotelStepCard = ({
  step,
  date,
  dayIndex,
  computeOptionalBenefitPrice,
  selectedOptions,
  setSelectedOptions,
  currency = 'EUR',
  creationDate,
  accessToken,
  isViewMode = false,
  adultParticipants,
  childParticipants,
  language,
}: EstimateDayHotelStepCardProps) => {
  const { t } = useTranslation();
  const {
    type,
    computeBenefitPriceDelta,
    stepDates,
    standardBenefitPrice,
    computeHotelRoomPriceForAllDates,
  } = useEstimatePrices({
    step,
    computeOptionalBenefitPrice,
    date,
    dayIndex,
  });

  const defaultSelectedOption = useMemo(
    () =>
      Object.hasOwn(selectedOptions, step.id) ? selectedOptions[step.id] : null,
    [selectedOptions, step.id],
  );

  const optionalBenefitsWithDeltaPrice = useMemo(
    () =>
      step.optionalBenefits
        .map((benefit) => {
          const deltaPrice = sanitizeDeltaPrice(
            computeBenefitPriceDelta(benefit),
          );
          return { benefit, deltaPrice };
        })
        .filter(({ benefit }) => benefit.id !== step.standardBenefit?.id)
        .toSorted((a, b) => a.deltaPrice - b.deltaPrice),
    [computeBenefitPriceDelta, step.optionalBenefits, step.standardBenefit?.id],
  );

  const {
    allHotelCategories,
    allRoomPrices,
    selectedHotelCategory,
    setSelectedHotelCategory,
    images,
    mainImageKey,
    standardPension,
    pensions,
    arePensionsGlobal,
  } = useEstimateHotelCategories({
    hotelBenefit: step.standardBenefit,
    integrationPensionType:
      step.standardBenefitPensionType ??
      step.standardBenefit?.hotel?.pricingPensionType,
    computeBenefitPriceDelta,
    computeHotelRoomPriceForAllDates,
    defaultSelectedOption,
    adultParticipants,
    childParticipants,
    standardBenefitPrice,
    nbDays: step.nbDays ?? 1,
    startDate: date,
    isStandardBenefit: true,
    currency,
    language,
  });

  const { data: imageUrl } = useGetFileUrl(mainImageKey, false, accessToken);

  const [selectedOptionalBenefitId, setSelectedOptionalBenefitId] = useState<
    string | null
  >(defaultSelectedOption?.benefitId ?? step.standardBenefit?.id ?? null);

  const [displayOptionalBenefits, setDisplayOptionalBenefits] = useState(
    !!selectedOptionalBenefitId &&
      selectedOptionalBenefitId !== step.standardBenefit?.id,
  );

  const standardName = useMemo(() => {
    const translations = language
      ? step.standardBenefit?.translations?.[language]
      : undefined;
    const stepName = translations?.name ?? step.standardBenefit?.name;
    return stepName
      ? `${stepName} ${step.standardBenefit?.hotel?.category ? `- ${getHotelCategoryLabel(t, step.standardBenefit?.hotel?.category)}` : ''}`
      : '';
  }, [step.standardBenefit, t, language]);

  const isStandardSelected = useMemo(
    () =>
      !selectedOptionalBenefitId ||
      selectedOptionalBenefitId === step.standardBenefit?.id ||
      selectedOptionalBenefitId === '',
    [selectedOptionalBenefitId, step.standardBenefit?.id],
  );

  const handleStandardBenefitClick = useCallback(() => {
    setSelectedOptionalBenefitId(step.standardBenefit?.id ?? '');
    setSelectedOptions((prev) => {
      const { [step.id]: _, ...rest } = prev;
      return rest;
    });
  }, [setSelectedOptions, step.id, step.standardBenefit?.id]);

  return (
    <RadioGroup
      disabled={isViewMode}
      defaultValue={
        defaultSelectedOption?.benefitId ?? step.standardBenefit?.id
      }
    >
      <EstimateDayStepCardTemplate
        step={step}
        type={type}
        stepDates={stepDates}
        standardName={standardName}
        displayOptionalBenefits={displayOptionalBenefits}
        setDisplayOptionalBenefits={setDisplayOptionalBenefits}
        imageUrl={imageUrl?.url}
        imagesKeys={images}
        accessToken={accessToken}
        currency={currency}
        language={language}
        radioGroupElement={
          <RadioGroupItem
            value={step.standardBenefit?.id ?? ''}
            onClick={handleStandardBenefitClick}
            checked={isStandardSelected}
          />
        }
        standardBenefitOptions={
          <EstimateHotelBenefitRoomOptions
            allHotelCategories={allHotelCategories}
            allRoomPrices={allRoomPrices}
            selectedHotelCategory={selectedHotelCategory}
            setSelectedHotelCategory={setSelectedHotelCategory}
            setSelectedOptions={setSelectedOptions}
            stepId={step.id}
            benefitId={step.standardBenefit?.id ?? ''}
            selectedOptions={selectedOptions}
            benefitDeltaPrice={0}
            shouldSetSelectedOptionsOnMount={false}
            selectCurrentHotel={() => {
              setSelectedOptionalBenefitId(step.standardBenefit?.id ?? '');
            }}
            standardPension={standardPension}
            pensions={pensions}
            arePensionsGlobal={arePensionsGlobal}
            isStandardHotel
            currency={currency}
            accessToken={accessToken}
            isViewMode={isViewMode}
          />
        }
      >
        {optionalBenefitsWithDeltaPrice.map(
          ({ benefit, deltaPrice }, indexBenefit) => (
            <HotelOptionalBenefitCell
              benefit={benefit}
              deltaPrice={deltaPrice}
              isChecked={selectedOptionalBenefitId === benefit.id}
              optionalDisplays={step.optionalDisplays}
              selectOptionalBenefit={() =>
                setSelectedOptionalBenefitId(benefit.id)
              }
              stepId={step.id}
              setSelectedOptions={setSelectedOptions}
              adultParticipants={adultParticipants}
              childParticipants={childParticipants}
              standardBenefitPrice={standardBenefitPrice}
              computeHotelRoomPriceForAllDates={
                computeHotelRoomPriceForAllDates
              }
              computeBenefitPriceDelta={computeBenefitPriceDelta}
              defaultSelectedOption={defaultSelectedOption}
              optionalBenefitPensionType={
                step.optionalBenefitsPensionTypes?.[benefit.id]
              }
              selectedOptions={selectedOptions}
              language={language}
              accessToken={accessToken}
              currency={currency}
              creationDate={creationDate}
              nbDays={step.nbDays ?? 1}
              startDate={date}
              isViewMode={isViewMode}
              key={`day-${dayIndex}-optionalBenefit-${indexBenefit}-${benefit.id}`}
            />
          ),
        )}
      </EstimateDayStepCardTemplate>
    </RadioGroup>
  );
};

export default EstimateDayHotelStepCard;

interface HotelOptionalBenefitCellProps
  extends
    EstimateOptionalBenefitCellProps,
    Omit<EstimateHotelCategoriesProps, 'hotelBenefit' | 'language'> {
  stepId: string;
  optionalBenefitPensionType?: string | null;
  selectedOptions: Record<string, SelectedEstimateOption>;
  setSelectedOptions: Dispatch<
    SetStateAction<Record<string, SelectedEstimateOption>>
  >;
  isViewMode?: boolean;
}

const HotelOptionalBenefitCell = ({
  benefit,
  deltaPrice,
  isChecked,
  optionalDisplays,
  selectOptionalBenefit,
  stepId,
  optionalBenefitPensionType,
  setSelectedOptions,
  selectedOptions,
  isViewMode = false,
  accessToken,
  currency = 'EUR',
  creationDate,
  language,
  ...props
}: HotelOptionalBenefitCellProps) => {
  const {
    allHotelCategories,
    allRoomPrices,
    selectedHotelCategory,
    setSelectedHotelCategory,
    mainImageKey,
    images,
    standardPension,
    pensions,
    arePensionsGlobal,
  } = useEstimateHotelCategories({
    hotelBenefit: benefit,
    integrationPensionType:
      optionalBenefitPensionType ?? benefit.hotel?.pricingPensionType,
    ...props,
    nbDays: props.nbDays ?? 1,
    currency,
    language,
  });

  // Same formula as the calculette after room-options mount: cheapest room
  // combination delta (keeps central chip and side card aligned).
  const roomCombinationDelta = useMemo(() => {
    const comboPrice = allRoomPrices[0]?.combinationPrice;
    return comboPrice !== undefined
      ? sanitizeDeltaPrice(comboPrice)
      : sanitizeDeltaPrice(deltaPrice);
  }, [allRoomPrices, deltaPrice]);

  const selectWithRoomCombinationDelta = useCallback(() => {
    selectOptionalBenefit();
    const hotelCategoryId = selectedHotelCategory?.id;
    setSelectedOptions((prev) => ({
      ...prev,
      [stepId]: {
        benefitId: benefit.id,
        type: BenefitType.HOTEL,
        deltaPrice: roomCombinationDelta,
        hotelCategoryId:
          hotelCategoryId == null ? undefined : hotelCategoryId,
        roomCombination: allRoomPrices[0]?.roomCombination,
        // deltaPrice has no pension supplement: never inherit the previous
        // hotel's pension.
        hotelPensionType: standardPension,
      } satisfies SelectedEstimateOption,
    }));
  }, [
    allRoomPrices,
    benefit.id,
    roomCombinationDelta,
    selectOptionalBenefit,
    selectedHotelCategory?.id,
    setSelectedOptions,
    standardPension,
    stepId,
  ]);

  return (
    <EstimateOptionalBenefitCell
      benefit={benefit}
      deltaPrice={roomCombinationDelta}
      isChecked={isChecked}
      optionalDisplays={optionalDisplays}
      selectOptionalBenefit={selectWithRoomCombinationDelta}
      mainImageKey={mainImageKey ?? undefined}
      imagesKeys={images}
      currency={currency}
      creationDate={creationDate}
      accessToken={accessToken}
      language={language}
    >
      {isChecked && (
        <div className="w-full">
          <EstimateHotelBenefitRoomOptions
            allHotelCategories={allHotelCategories}
            allRoomPrices={allRoomPrices}
            selectedHotelCategory={selectedHotelCategory}
            setSelectedHotelCategory={setSelectedHotelCategory}
            setSelectedOptions={setSelectedOptions}
            stepId={stepId}
            benefitId={benefit.id}
            selectedOptions={selectedOptions}
            selectCurrentHotel={() => {}}
            benefitDeltaPrice={roomCombinationDelta}
            standardPension={standardPension}
            pensions={pensions}
            arePensionsGlobal={arePensionsGlobal}
            currency={currency}
            accessToken={accessToken}
            isViewMode={isViewMode}
          />
        </div>
      )}
    </EstimateOptionalBenefitCell>
  );
};
